
import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'

interface ProtectedRouteProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export default function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { user, isLoading, refreshAuth, syncWithStorage } = useAuth()
  const navigate = useNavigate()
  const hasTriedSync = useRef(false)
  const hasTriedRefresh = useRef(false)

  useEffect(() => {
    // Wait for auth loading to complete
    if (isLoading) return

    // If no user, try quick sync first (handles race condition with AuthContainer)
    if (!user && !hasTriedSync.current) {
      hasTriedSync.current = true

      const syncSuccess = syncWithStorage()
      if (!syncSuccess) {
        // Only do expensive refresh if quick sync fails
        if (!hasTriedRefresh.current) {
          hasTriedRefresh.current = true

          refreshAuth().then(() => {
            // After refresh, if still no user, redirect
            const storedProfile = localStorage.getItem('user-profile')
            if (!storedProfile) {
              navigate({ to: '/login', replace: true })
            }
          }).catch((error) => {
            console.error('❌ Auth refresh failed:', error)
            navigate({ to: '/login', replace: true })
          })
        }
      }
      return
    }

    // If still no user after both sync and refresh attempts, redirect
    if (!user && hasTriedSync.current && hasTriedRefresh.current) {
      navigate({ to: '/login', replace: true })
      return
    }
  }, [user, isLoading, navigate, refreshAuth, syncWithStorage])

  // Show loading while checking auth
  if (isLoading) {
    return (
      fallback || (
        <div className="min-h-screen bg-brand-dark flex items-center justify-center relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/10 via-transparent to-brand-blue/5" />
          </div>
          <div className="text-center relative z-10 animate-fade-in-up">
            <div className="animate-spin rounded-full h-32 w-32 border-4 border-brand-primary/20 border-t-brand-primary mx-auto mb-6 shadow-monumenta"></div>
            <h2 className="font-display text-2xl font-bold text-white mb-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">Carregando...</h2>
            <p className="text-brand-gray-light font-body">Verificando autenticação</p>
          </div>
        </div>
      )
    )
  }

  // If user exists, render children
  if (user) {
    return <>{children}</>
  }

  // No user and not loading - redirect handled by useEffect
  return null
}